import { useCallback, useMemo, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';

import { StandardDragDropList, DragDropManager } from '@pega/cosmos-react-dnd';
import type {
  StandardDragDropListItemProps,
  StandardDragDropListProps
} from '@pega/cosmos-react-dnd';
import { Button, Flex, Icon, useElement } from '@pega/cosmos-react-core';
import type { MultiSelectInputProps } from '@pega/cosmos-react-core/lib/components/ComboBox/MultiSelectInput/MultiSelectInput.types';

import { mockQuery, mockFields, mockGroups, mockViews } from './mockData';
import type { MockRawItem } from './mockData';
import ItemConfig from './ItemConfig';
import SelectionMenu from './SelectionMenu';
import type { SelectionMenuProps } from './SelectionMenu';

export interface StandardDragDropListDemoProps {
  items: MockRawItem[];
  draggable?: boolean;
  info?: ReactNode;
  icons?: boolean;
  onConfigure?: boolean;
  onRemove?: boolean;
  onAddTo?: boolean;
  selectable?: boolean;
  footer?: boolean;
  onSelectionChange?: () => void;
}

export default ({
  items,
  draggable,
  info,
  icons,
  onConfigure,
  onRemove,
  onAddTo,
  selectable,
  footer,
  onSelectionChange
}: StandardDragDropListDemoProps): JSX.Element => {
  // The state of the list's content is maintained here and the raw item objects are transformed below prior to render.
  const [mockRawItems, setMockRawItems] = useState<MockRawItem[]>(items);

  const [configItemId, setConfigItemId] = useState<MockRawItem['id'] | null>(null);

  const [configTarget, setConfigTarget] = useElement(null);

  const [selectionMenuTarget, setSelectionMenuTarget] = useElement(null);

  const [selectionConstraint, setSelectionConstraint] =
    useState<SelectionMenuProps['constraint']>();

  const [addToItemId, setAddToItemId] = useState<MockRawItem['id'] | null>(null);

  const [errors, setErrors] = useState<{ rawItemId: MockRawItem['id']; message: string }[]>([]);

  const onAddContent = useCallback(
    async (selected: NonNullable<MultiSelectInputProps['selected']>) => {
      setSelectionMenuTarget(null);
      setSelectionConstraint(undefined);

      if (selected.length === 0) return;

      const addedItems = await mockQuery({ id: selected.map(({ id }) => id) });

      setMockRawItems(cur => {
        if (addToItemId) {
          return cur.map(item => {
            if (item.id === addToItemId && item.items) {
              return {
                ...item,
                items: [
                  ...item.items,
                  ...addedItems.map(nestedItem => ({
                    ...nestedItem,
                    id: `${addToItemId}:${nestedItem.id}`
                  }))
                ]
              };
            }
            return item;
          });
        }

        return [...cur, ...addedItems.filter(newItem => !cur.find(({ id }) => id === newItem.id))];
      });
    },
    [addToItemId, setSelectionMenuTarget]
  );

  const onItemConfigure = useCallback(
    (id: StandardDragDropListItemProps['id'], e: MouseEvent<HTMLButtonElement>) => {
      setConfigTarget(e.currentTarget);
      setConfigItemId(id);
    },
    [setConfigTarget]
  );

  const onItemRemove = useCallback((id: StandardDragDropListItemProps['id']) => {
    setMockRawItems(cur =>
      cur.filter(rawItem => {
        if (rawItem.id === id) return false;
        // See if it's there before setting a new array instance.
        if (rawItem.items && rawItem.items.find(ri => ri.id === id)) {
          rawItem.items = rawItem.items.filter(ri => ri.id !== id);
        }
        return true;
      })
    );
  }, []);

  const onAddToGroup = useCallback(
    (groupId: StandardDragDropListItemProps['id'], e: MouseEvent<Element>) => {
      setAddToItemId(groupId);
      setSelectionMenuTarget(e.currentTarget);
      setSelectionConstraint({ types: ['Field'] });
    },
    [setSelectionMenuTarget]
  );

  const showSelectionMenu = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      setSelectionMenuTarget(e.currentTarget);
    },
    [setSelectionMenuTarget]
  );

  const onChange = useCallback<NonNullable<StandardDragDropListProps['onChange']>>(updatedItems => {
    setMockRawItems(cur => {
      const findRawItem = (id: string) => {
        for (let i = 0; i < cur.length; i += 1) {
          if (cur[i].id === id) return cur[i];

          if (cur[i].items) {
            const subItem = cur[i].items!.find(s => s.id === id);
            if (subItem) return subItem;
          }
        }

        return [...mockFields, ...mockViews, ...mockGroups].find(rawItem => rawItem.id === id)!;
      };

      return updatedItems.map(item => {
        const rawItem = findRawItem(item.id);

        if (item.items) {
          const rawSubItems = item.items.map(subItem => {
            return findRawItem(subItem.id);
          });

          return { ...rawItem, items: rawSubItems };
        }

        return rawItem;
      });
    });
  }, []);

  const createListItems = useCallback(
    (mci: MockRawItem[], onATG: typeof onAddToGroup): StandardDragDropListProps['items'] => {
      return mci.map(rawItem => {
        const itemError = errors?.find(error => error.rawItemId === rawItem.id);

        const primary =
          rawItem.type === 'View'
            ? {
                text: rawItem.label,
                href: '/some-url',
                onClick: (e: MouseEvent) => {
                  e.preventDefault();
                  // Navigate or present a dialog/panel for clicked item...
                }
              }
            : rawItem.label;

        const baseItem: StandardDragDropListItemProps = {
          // type property is being spread in via the mock content item.
          ...rawItem,
          primary,
          draggable,
          info,
          secondary: rawItem.subType ?? rawItem.type,
          status: itemError ? { type: 'error', message: itemError.message } : undefined,
          onConfigure: onConfigure ? onItemConfigure : undefined,
          onRemove: onRemove ? onItemRemove : undefined,
          icon: icons ? rawItem.icon : undefined,
          items: undefined
        };

        return rawItem.type === 'Group'
          ? {
              ...baseItem,
              items: rawItem.items ? createListItems(rawItem.items, onATG) : [],
              accept: 'Field',
              onAddTo: onAddTo ? onATG : undefined
            }
          : baseItem;
      });
    },
    [
      errors,
      onItemConfigure,
      onItemRemove,
      draggable,
      info,
      icons,
      onConfigure,
      onRemove,
      onAddTo,
      footer,
      selectable
    ]
  );

  // Here we transform MockRawItems -> StandardDragDropListProps['items']
  const itemsToRender: StandardDragDropListProps['items'] = useMemo(() => {
    return createListItems(mockRawItems, onAddToGroup);
  }, [mockRawItems, onAddToGroup, createListItems]);

  const handleSelectionChange = (item: StandardDragDropListItemProps) => {
    const updateItemSelection = (rawItem: MockRawItem) => {
      if (rawItem.id === item.id) return { ...rawItem, selected: item.selected };
      if (rawItem.items?.find(ri => ri.id === item.id)) {
        return {
          ...rawItem,
          items: rawItem.items.map(ri =>
            ri.id === item.id ? { ...ri, selected: item.selected } : ri
          )
        };
      }
      return rawItem;
    };
    setMockRawItems(cur => cur.map(updateItemSelection));
    onSelectionChange?.();
  };

  return (
    <>
      <DragDropManager>
        {useMemo(
          () => (
            <StandardDragDropList
              accept={['Field', 'View', 'Group']}
              heading='Mock view'
              info={info}
              items={itemsToRender}
              onChange={onChange}
              onItemSelection={selectable ? handleSelectionChange : undefined}
              footer={
                footer ? (
                  <Button onClick={showSelectionMenu} variant='link'>
                    <Flex container={{ inline: true, alignItems: 'center', gap: 1 }}>
                      <Icon name='plus' />
                      <span>Add</span>
                    </Flex>
                  </Button>
                ) : undefined
              }
            />
          ),
          [itemsToRender, showSelectionMenu, onChange]
        )}
      </DragDropManager>
      {configTarget && configItemId && (
        <ItemConfig
          itemId={configItemId}
          target={configTarget}
          onCancel={() => {
            setConfigTarget(null);
            setConfigItemId(null);
            setErrors(currentErrors => {
              const currentItemError = currentErrors?.find(err => err.rawItemId === configItemId);

              return currentItemError
                ? currentErrors.filter(err => err.rawItemId !== configItemId)
                : [
                    ...currentErrors,
                    { rawItemId: configItemId, message: 'Misconfiguration detected' }
                  ];
            });
          }}
          onSubmit={() => {
            setConfigTarget(null);
            setConfigItemId(null);
          }}
        />
      )}
      {selectionMenuTarget && (
        <SelectionMenu
          target={selectionMenuTarget}
          constraint={selectionConstraint}
          onCancel={() => {
            setSelectionMenuTarget(null);
            setAddToItemId(null);
            setSelectionConstraint(undefined);
          }}
          onSubmit={onAddContent}
        />
      )}
    </>
  );
};
